import { Fragment, useMemo, useRef, useState } from 'react';
import arrowDown from '~/assets/icons/arrow_down.svg';
import clock from '~/assets/icons/clock.svg';
import craft from '~/assets/icons/craft.svg';
import { CollectionImage } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { СountSlider } from '../СountSlider';
import {
	StyledButtonContent,
	StyledContainer,
	StyledCountContainer,
	StyledCountSliderContainer,
	StyledCraftContainer,
	StyledDescription,
	StyledHeadBackground,
	StyledHeadContainer,
	StyledItem,
	StyledItemContainer,
	StyledItemsGrid,
} from './CraftMenu.styled';

const CraftMenu = () => {
	const playerIngredients = [44, 10, 15, 55, 3];
	const requiredIngredients = [2, 1, 3, 2, 1];
	const [sliderValue, setSliderValue] = useState(1);
	const [isCrafting, setIsCrafting] = useState(false);
	const [timeLeft, setTimeLeft] = useState(30);
	const timeRef = useRef<HTMLDivElement>(null);

	const handleCraft = () => {
		setIsCrafting(true);
		setTimeLeft(30);

		const interval = setInterval(() => {
			setTimeLeft((prev) => {
				if (prev <= 1) {
					clearInterval(interval);
					setIsCrafting(false);
					return 30;
				}
				return prev - 1;
			});
		}, 1000);
	};

	const maxCraftable = useMemo(() => {
		if (playerIngredients.length === 0 || requiredIngredients.length === 0)
			return 1;

		let max = Math.floor(playerIngredients[0] / requiredIngredients[0]);

		for (let i = 1; i < playerIngredients.length; i++) {
			const possibleForThisIngredient = Math.floor(
				playerIngredients[i] / requiredIngredients[i],
			);
			max = Math.min(max, possibleForThisIngredient);
		}

		return Math.max(0, max);
	}, [playerIngredients, requiredIngredients]);

	const canCraft = maxCraftable > 0;

	const getRequiredForCurrentCraft = (ingredientIndex: number) => {
		return requiredIngredients[ingredientIndex] * sliderValue;
	};

	const hasEnoughIngredient = (ingredientIndex: number) => {
		return (
			playerIngredients[ingredientIndex] >=
			getRequiredForCurrentCraft(ingredientIndex)
		);
	};

	const handleSliderChange = (value: number) => {
		if (canCraft) {
			setSliderValue(value);
		}
	};

	return (
		<StyledContainer>
			<StyledHeadContainer>
				<StyledHeadBackground />
				<Text>Приманка &ldquo;Опарыши&rdquo;</Text>
			</StyledHeadContainer>
			<StyledDescription>
				Приманка &ldquo;Опарыши&rdquo; нужна на секретных точках рыбалке, где вы
				сможете поймать редкую рыбу
			</StyledDescription>
			<StyledCraftContainer>
				<SectionHeading text="Предметы для крафта" icon={craft} />
				<StyledItemsGrid>
					{new Array(5).fill(0).map((_, index) => {
						const required = getRequiredForCurrentCraft(index);
						const hasEnough = hasEnoughIngredient(index);

						return (
							<StyledItemContainer key={index}>
								<StyledItem>
									<CollectionImage
										collection="items"
										id="baconizer"
										size={50}
									/>
								</StyledItem>
								<img src={arrowDown} className="arrow" />
								<StyledCountContainer $red={!hasEnough}>
									<Text>
										{playerIngredients[index]} <span>/ {required}</span>
									</Text>
								</StyledCountContainer>
							</StyledItemContainer>
						);
					})}
				</StyledItemsGrid>
				<StyledCountSliderContainer>
					<СountSlider
						min={canCraft ? 1 : 0}
						max={canCraft ? maxCraftable : 1}
						sliderValue={canCraft ? sliderValue : 0}
						count={canCraft ? sliderValue : 0}
						onChange={handleSliderChange}
						disabled={!canCraft}
					/>
				</StyledCountSliderContainer>
				<Text
					style={{
						fontSize: 14,
						color: '#ffffff',
						opacity: 0.25,
						marginTop: 20,
					}}
				>
					Все предметы будут добавлены в инвентарь вашего дома.
				</Text>
				<Button
					height={ButtonHeight.Height50}
					color={ButtonColors.Select2}
					onClick={handleCraft}
					disabled={!canCraft && !isCrafting}
					style={{
						fontSize: 16,
						marginTop: 58,
						width: 240,
						backgroundColor: isCrafting ? 'rgba(255, 255, 255, 0.05)' : '',
						pointerEvents: isCrafting ? 'none' : 'auto',
					}}
				>
					<StyledButtonContent>
						{isCrafting && (
							<Fragment>
								<Text style={{ opacity: 0.5 }}>Осталось</Text>
								<img src={clock} />
								<Text ref={timeRef} style={{ fontWeight: 500 }}>
									{timeLeft} сек.
								</Text>
							</Fragment>
						)}
						{!canCraft && !isCrafting && <Text>У вас нехватает предметов</Text>}
						{canCraft && !isCrafting && (
							<Fragment>
								<Text>Скрафтить</Text>
								<img src={clock} />
								<Text style={{ fontWeight: 500 }}>30 сек.</Text>
							</Fragment>
						)}
					</StyledButtonContent>
				</Button>
			</StyledCraftContainer>
		</StyledContainer>
	);
};

export { CraftMenu };
